import React from "react";
import { createPortal } from "react-dom";
import { GlobalStyles } from "@mui/material";
import { ReportData } from "../types";
import {
  MONTH_STATUS_LABEL,
  PARTNER_SETUP_INCOMPLETE,
  partnerSection,
  reconciliationRows,
  reportTitle,
} from "../utils/reportSections";
import { PRINT_ROOT_CLASS, printStyles } from "../utils/printStyles";
import PrintSummaryBlock from "./PrintSummaryBlock";

/**
 * The printed financial report — never the transaction ledger.
 *
 * Rendered straight into <body> (a portal) so the print stylesheet can hide
 * the whole app — sidebar, top bar, period controls, buttons, ledger, dialogs
 * — and print only this. Same section order and values as the PDF: it reads
 * the same normalized ReportData. Works for every period mode.
 */
const PrintableReport: React.FC<{ report: ReportData }> = ({ report }) => {
  const rec = reconciliationRows(report);
  const partners = partnerSection(report);
  const generated = new Date().toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" });

  return createPortal(
    <div className={PRINT_ROOT_CLASS} data-testid="printable-report">
      <GlobalStyles styles={printStyles} />
      <header className="pr-header">
        <h1>{report.project.name}</h1>
        <h2>{reportTitle(report)}</h2>
        <p>
          {report.period.label}
          {report.monthly && ` · Status: ${MONTH_STATUS_LABEL[report.monthly.month.status]}`}
        </p>
      </header>

      <PrintSummaryBlock figures={report} />

      <section className="pr-section">
        <h3>Reconciliation</h3>
        <table className="pr-table">
          <thead>
            <tr>
              <th>Calculated Balance</th>
              <th>Actual Balance</th>
              <th>Difference</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{rec.calculated}</td>
              <td>{rec.actual}</td>
              <td>{rec.difference}</td>
              <td className={`pr-status-${rec.statusKey}`}>{rec.status}</td>
            </tr>
          </tbody>
        </table>
        {rec.statusKey === "monthly_only" && rec.note && <p className="pr-note">{rec.note}</p>}
      </section>

      {partners.state !== "not_applicable" && (
        <section className="pr-section">
          <h3>Partner Contribution / Mortgage Share</h3>
          {partners.state === "ready" ? (
            <>
              <table className="pr-table">
                <thead>
                  <tr>
                    {partners.table.headers.map((h, i) => (
                      <th key={h} className={i ? "pr-num" : undefined}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partners.table.rows.map((r) => (
                    <tr key={r.label} className={r.emphasis ? "pr-emphasis" : undefined}>
                      <td>{r.label}</td>
                      {r.cells.map((c, i) => (
                        <td key={i} className="pr-num">
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pr-payments">
                <div>
                  <span>Monthly Mortgage</span>
                  <strong>{partners.table.mortgage}</strong>
                </div>
                {partners.table.payments.map((p) => (
                  <div key={p.name}>
                    <span>{p.name} to Pay</span>
                    <strong>{p.amount}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="pr-warning">
              <strong>{PARTNER_SETUP_INCOMPLETE}</strong>
              <ul>
                {partners.missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <footer className="pr-footer">
        <span>
          {report.project.name} · {report.period.label}
        </span>
        <span>Generated {generated}</span>
      </footer>
    </div>,
    document.body
  );
};

export default PrintableReport;
